import { useState, useEffect } from "react";
import { CalendarDays, ArrowRight } from "lucide-react";
import GhostContentAPI from "@tryghost/content-api";

// Ghost Content API Initialization
const api = new GhostContentAPI({
  url: "https://trendhopper.ghost.io",
  key: "587865eb8bff414e953a4e3e99",
  version: "v5.0",
});

export default function BlogPreview() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const recentPosts = await api.posts.browse({ limit: 3, include: "html" });
        setPosts(recentPosts);
        setSelectedPost(recentPosts[0]); // Default to the first post
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };
    fetchPosts();
  }, []);

  if (!posts.length) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <section 
    id="examples"
    className="bg-slate-900 py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-16">
          <p className="text-[#FF6B4A] text-sm font-semibold tracking-wider mb-4">
            TRENDING INSIGHTS
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
          Engaging AI-generated content <span className="text-[#FF6B4A]"> that captivates readers.</span>
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Side - Blog List */}
          <div className="w-full lg:w-1/3 space-y-4">
            {posts.map((post) => (
              <button
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                  selectedPost?.id === post.id
                    ? "bg-slate-800/70 border-l-4 border-[#FF6B4A]"
                    : "bg-slate-800/50 hover:bg-slate-800/60"
                }`}
              >
                <div className="flex items-center gap-4 text-[#FF6B4A] mb-2 text-sm">
                  <CalendarDays className="w-4 h-4" />
                  <span>{new Date(post.published_at).toLocaleDateString()}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-slate-400 text-sm line-clamp-2">{post.excerpt}</p>
              </button>
            ))}
          </div>

          {/* Right Side - Display ONLY the Selected Post */}
          {selectedPost && (
            <div className="w-full lg:w-2/3 bg-slate-800/50 rounded-xl overflow-hidden flex flex-col">
              {/* Scrollable Article Content */}
              <div className="p-6 overflow-y-auto max-h-[500px]">
                <div className="flex items-center gap-4 text-[#FF6B4A] mb-4 text-sm">
                  <CalendarDays className="w-4 h-4" />
                  <span>{new Date(selectedPost.published_at).toLocaleDateString()}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{selectedPost.title}</h3>

                {/* Full Article Content */}
                <div
                    className="prose prose-invert max-w-none [&_img]:w-3/4 [&_img]:mx-auto [&_img]:rounded-lg [&_img]:p-4 [&_figure]:text-center [&_figcaption]:text-sm [&_figcaption]:text-slate-400 [&_figcaption]:mt-2"
                    dangerouslySetInnerHTML={{ __html: selectedPost.html }}
                    />

                {/* Read Full Article Link */}
                <a
                  href={`/blog/${selectedPost.slug}`}
                  className="inline-flex items-center text-[#FF6B4A] text-sm font-medium group mt-6"
                >
                  Read on Blog
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
